import { useEffect, useState, useRef, useContext } from 'react';
import { Dumbbell, MapPin, Clock, MessageCircle } from 'lucide-react';
import api from "../../utils/api";
import { AuthContext } from '../../context/AuthContext';

const GymFeed = ({ onFeedLoaded, activeGymcode }) => {
  const { user: authUser } = useContext(AuthContext);
  const [gymInfo, setGymInfo] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageDimensions, setImageDimensions] = useState({});
  const containerRef = useRef(null);

  const currentGymcode = activeGymcode || localStorage.getItem('fitscan_active_gymcode') || authUser?.gymcode;

  const fetchGymFeed = async () => {
    if (!currentGymcode || currentGymcode.trim() === '') 
      return { posts: [], gym: null };
    try {
      const response = await api.post('/api/Gym/gymfeed', { gymcode: currentGymcode });
      return response.data;
    } catch (error) {
      console.error('gymfeed error:', error.response?.data || error.message);
      return { posts: [], gym: null };
    }
  };

  const loadFeed = async () => {
    setLoading(true);
    const postsData = await fetchGymFeed();
    const fetchedPosts = postsData.posts || [];
    setPosts(fetchedPosts);
    setGymInfo(postsData.gym);
    setLoading(false);
    // Report newest post date to parent
    if (onFeedLoaded && fetchedPosts.length > 0) {
      onFeedLoaded(fetchedPosts[0].postedOn); // already sorted descending
    }
  };

  useEffect(() => {
    if (!currentGymcode) return;
    loadFeed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGymcode]);



  const isCloudinaryVideo = (url) => {
    if (!url) return false;
    return url.includes('player.cloudinary.com/embed');
  };

  const getAspectRatio = (postId) => {
    const dims = imageDimensions[postId];
    if (!dims) return 'aspect-square';

    const ratio = dims.width / dims.height;

    if (ratio > 1.3) return 'aspect-video'; // 16:9 landscape
    if (ratio < 0.8) return 'aspect-[9/16]'; // 9:16 portrait
    return 'aspect-square'; // 1:1 square
  };

  const handleImageLoad = (postId, img) => {
    setImageDimensions(prev => ({
      ...prev,
      [postId]: {
        width: img.naturalWidth,
        height: img.naturalHeight
      }
    }));
  };

  // Get relative time (e.g., "2h ago")
  const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  // Skeleton Loader Card
  const SkeletonCard = () => (
    <div className="bg-[#181818] border border-[#2b2b2b] rounded-2xl overflow-hidden animate-pulse">
      <div className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 bg-[#2b2b2b] rounded-full" />
        <div className="space-y-2 flex-1">
          <div className="h-4 bg-[#2b2b2b] rounded w-1/3" />
          <div className="h-3 bg-[#2b2b2b] rounded w-1/4" />
        </div>
      </div>
      <div className="h-64 bg-[#2b2b2b]" />
      <div className="p-4 space-y-3">
        <div className="h-4 bg-[#2b2b2b] rounded w-3/4" />
        <div className="h-4 bg-[#2b2b2b] rounded w-1/2" />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="max-w-xl mx-auto space-y-6">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (!posts.length) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 bg-[#181818] rounded-full flex items-center justify-center mb-4">
          <MessageCircle size={32} className="text-gray-500" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">No updates yet</h3>
        <p className="text-gray-500 max-w-xs">
          When your gym posts updates, they will appear here in your feed.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto space-y-8 pb-10" ref={containerRef}>
      {posts.map((post) => (
        <article
          key={post.id}
          className="bg-[#181818] border border-[#2b2b2b] rounded-2xl overflow-hidden shadow-xl"
        >
          {/* Post Header */}
          <header className="p-4 flex items-center justify-between border-b border-[#2b2b2b]/50">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[#6CD800]/10 flex items-center justify-center border border-[#6CD800]/20 overflow-hidden">
                {gymInfo?.logoUrl ? (
                  <img src={gymInfo.logoUrl} alt={gymInfo.name} className="w-full h-full object-cover" />
                ) : (
                  <Dumbbell className="text-[#6CD800]" size={20} />
                )}
              </div>
              <div>
                <h3 className="text-sm font-bold text-white leading-tight">
                  {gymInfo?.name || 'Your Gym'}
                </h3>
                <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mt-0.5">
                  <Clock size={12} />
                  <span>{getRelativeTime(post.postedOn)}</span>
                  {post.location && (
                    <>
                      <span className="w-1 h-1 rounded-full bg-gray-700" />
                      <MapPin size={12} />
                      <span>{post.location}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </header>

          {/* Post Media */}
          {post.imageUrl && (
            <div className={`relative bg-black group ${getAspectRatio(post.id)}`}>
              {isCloudinaryVideo(post.imageUrl) ? (
                <iframe
                  src={post.imageUrl}
                  className="w-full h-full"
                  allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                  allowFullScreen
                  title="Gym Video"
                />
              ) : (
                <img
                  src={post.imageUrl}
                  alt={post.title || 'Gym update'}
                  className="w-full h-full object-contain"
                  onLoad={(e) => handleImageLoad(post.id, e.target)}
                />
              )}
            </div>
          )}

          {/* Post Content */}
          <div className="p-5 space-y-3">
            {post.title && (
              <h2 className="text-lg font-bold text-white leading-snug">
                {post.title}
              </h2>
            )}
            {post.description && (
              <p className="text-gray-400 text-[14px] leading-relaxed whitespace-pre-wrap">
                {post.description}
              </p>
            )}
          </div>
        </article>
      ))}
    </div>
  );
};

export default GymFeed;
